import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faCheckCircle, faHourglassHalf, faInfoCircle, faSync, faTruck,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import ListLoadError from '../../components/ListLoadError';
import logo from '../../assets/images/IMT_logo.png';
import { AuthContext } from '../../context/AuthContext';
import { hasFinancialAccess } from '../../utils/financialAccess';
import { extractApiError } from '../../utils/errorMessages';
import {
  fetchBunkerInvoiceApprovals,
  invoicePath,
} from '../../services/bunkerInvoiceApprovalsService';
import '../../components/ui/HMSHeaderStyles.css';
import '../../components/ui/HMSTabs.css';
import './OutgoingInvoice.css';
import './BunkerInvoiceApprovals.css';

/**
 * Bunker Invoice Approvals (ClickUp 2026-09-17, "Deliveries versus invoices").
 *
 * On a bunker delivery, invoices are issued (outgoing) and captured (incoming)
 * and saved as drafts only. The Bunker Operations Approvals group comes here,
 * double-checks them, and sends them to the financial approvers with Save &
 * Workflow on each invoice.
 *
 * Built from the Financials list screens' own parts -- the HMS screen header,
 * the icon tab card, `invoice-list-table`, `status-badge`, the loading, empty
 * and load-error states and the footer -- so it reads as one of them. One row
 * per bunker delivery: ENQ, Delivery Nr., then the outgoing and incoming
 * invoices side by side (partner, amount, status). Everything the server
 * returns is drawn -- there are no pages -- and the toolbar says how many
 * deliveries are showing out of how many, so a short list can never pass for a
 * complete one. A load failure is its own state, never "nothing waiting".
 */

const VIEWS = {
  waiting: 'waiting',
  all: 'all',
};

export const formatAmount = (value, currency) => {
  const number = parseFloat(value);
  const amount = Number.isNaN(number)
    ? value
    : number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency || ''} ${amount}`.trim();
};

const withCount = (label, count) => (typeof count === 'number' ? `${label} (${count})` : label);

const InvoiceCells = ({ invoice, onOpen, emptyLabel }) => {
  if (!invoice) {
    return emptyLabel
      ? <td className="bia-none" colSpan={3}>{emptyLabel}</td>
      : <td className="bia-blank" colSpan={3} />;
  }
  return (
    <>
      <td className="bia-partner-cell">
        {invoicePath(invoice) ? (
          <button
            type="button"
            className="bia-invoice-link"
            onClick={() => onOpen(invoice)}
            title={invoice.reference ? `Open ${invoice.reference}` : 'Open invoice'}
          >
            {invoice.partner_name || 'No partner yet'}
          </button>
        ) : (
          // A kind with no screen here: the name, not a click that does nothing.
          <span className="bia-invoice-link">{invoice.partner_name || 'No partner yet'}</span>
        )}
        {invoice.reference && <div className="bia-reference">{invoice.reference}</div>}
      </td>
      <td className="amount-cell">{formatAmount(invoice.gross_amount, invoice.currency)}</td>
      <td className="status-cell">
        <span className={`status-badge ${String(invoice.status || '').toLowerCase()}`}>
          {invoice.status_display}
        </span>
      </td>
    </>
  );
};

const DeliveryRows = ({ row, isLastDelivery, onOpen, onOpenDelivery }) => {
  const lines = Math.max(row.outgoing.length, row.incoming.length, 1);
  return Array.from({ length: lines }, (_, index) => (
    <tr
      key={`${row.delivery_id}-${index}`}
      className={[
        index === lines - 1 ? 'bia-delivery-end' : '',
        isLastDelivery ? 'bia-last-delivery' : '',
      ].filter(Boolean).join(' ')}
      data-testid={index === 0 ? `delivery-row-${row.delivery_id}` : undefined}
    >
      {index === 0 && (
        <>
          <td className="invoice-number-cell bia-enq" rowSpan={lines}>{row.enquiry_number || '—'}</td>
          <td className="bia-delivery" rowSpan={lines}>
            <button type="button" className="bia-invoice-link" onClick={() => onOpenDelivery(row)}>
              {row.delivery_number || `#${row.delivery_id}`}
            </button>
            {row.vessel_name && <div className="bia-reference">{row.vessel_name}</div>}
          </td>
        </>
      )}
      <InvoiceCells
        invoice={row.outgoing[index]}
        onOpen={onOpen}
        emptyLabel={index === 0 && row.outgoing.length === 0 ? 'None yet' : null}
      />
      <InvoiceCells
        invoice={row.incoming[index]}
        onOpen={onOpen}
        emptyLabel={index === 0 && row.incoming.length === 0 ? 'None yet' : null}
      />
    </tr>
  ));
};

const BunkerInvoiceApprovals = () => {
  const history = useHistory();
  const { user } = useContext(AuthContext) || {};
  const [view, setView] = useState(VIEWS.waiting);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async (nextView) => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await fetchBunkerInvoiceApprovals(nextView));
    } catch (error) {
      console.error('Error loading bunker invoice approvals:', error);
      setData(null);
      setLoadError(extractApiError(error, 'The bunker invoice list could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(view);
  }, [load, view]);

  // This screen lives under Financials -> Invoicing (2026-09-17), so Back goes to
  // the Invoicing dashboard, like the invoice lists beside it. Invoicing is
  // finance-only, so an approver without it goes to the bunkers desk they do work
  // on, and anyone with neither to their own dashboard -- never to a door that
  // refuses them.
  const financeDoorOpen = hasFinancialAccess(user);
  const hasBunkersModule = Boolean(
    user && (user.has_full_access || user.has_bunkers_access || user.is_superuser || user.is_staff),
  );
  const backTo = financeDoorOpen
    ? '/invoicing/dashboard'
    : (hasBunkersModule ? '/dashboard/trades/fuels' : '/dashboard');
  const backTitle = financeDoorOpen
    ? 'Back to Invoicing Dashboard'
    : (hasBunkersModule ? 'Back to Trade Ops Bunkers' : 'Back to Dashboard');
  const goBack = () => history.push(backTo);

  const openInvoice = (invoice) => {
    const path = invoicePath(invoice);
    if (path) history.push(path);
  };
  const openDelivery = (row) => history.push(`/deliveries/${row.delivery_id}`);

  const rows = data?.results || [];

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container bunker-invoice-approvals-screen">
        <div className="hms-screen-header">
          <button
            type="button"
            className="invoice-header-back-button"
            onClick={goBack}
            title={backTitle}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="header-logo">
            <img src={logo} alt="IMT" className="header-logo-img" />
            <div className="header-title-content">
              <h1 className="hms-screen-title">BUNKER INVOICE APPROVALS</h1>
              <p className="invoice-subtitle">Check bunker delivery invoices and send them to the financial approvers</p>
            </div>
          </div>
        </div>

        <div className="bia-tabs-row">
          <div className="hms-tabs">
            <div className="tabs-container" role="tablist" aria-label="Which bunker deliveries to show">
              <button
                type="button"
                role="tab"
                aria-selected={view === VIEWS.waiting}
                className={`tab-button ${view === VIEWS.waiting ? 'active' : ''}`}
                onClick={() => setView(VIEWS.waiting)}
              >
                <div className="tab-icon"><FontAwesomeIcon icon={faHourglassHalf} /></div>
                <span>{withCount('Waiting for Check', data?.waiting_deliveries)}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === VIEWS.all}
                className={`tab-button ${view === VIEWS.all ? 'active' : ''}`}
                onClick={() => setView(VIEWS.all)}
              >
                <div className="tab-icon"><FontAwesomeIcon icon={faTruck} /></div>
                <span>{withCount('All Bunker Deliveries', data?.total_deliveries)}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="search-filter-controls bia-toolbar">
          <div className="bia-summary" data-testid="bia-summary">
            {data && !loading && (
              <>
                Showing <strong>{data.count}</strong> of <strong>{data.total_deliveries}</strong> bunker deliveries
                {' · '}<strong>{data.waiting_invoices}</strong>
                {' '}{data.waiting_invoices === 1 ? 'invoice' : 'invoices'} waiting for check
              </>
            )}
          </div>
          <button
            type="button"
            className="reload-button"
            onClick={() => load(view)}
            title="Reload bunker invoices"
            aria-label="Reload bunker invoices"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>

        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={() => load(view)} />

          {data && !data.can_send_to_finance && (
            <div className="bia-notice" role="note">
              <FontAwesomeIcon icon={faInfoCircle} className="bia-notice-icon" />
              <div>
                <strong>View only.</strong> You can see this list. Sending an invoice to finance
                needs the Bunker Operations Approver role.
              </div>
            </div>
          )}

          {loading && (
            <div className="loading-state">
              <div className="loading-spinner" />
              <p>Loading bunker invoices...</p>
            </div>
          )}

          {!loading && !loadError && data && (
            <div className="bia-table-scroll">
              <table className="invoice-list-table bia-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>ENQ</th>
                    <th rowSpan={2}>Delivery Nr.</th>
                    <th colSpan={3} className="bia-group-head">Outgoing Invoices (Issued)</th>
                    <th colSpan={3} className="bia-group-head">Incoming Invoices (Captured)</th>
                  </tr>
                  <tr className="bia-sub-head">
                    <th>Partner</th>
                    <th className="bia-amount-head">Amount</th>
                    <th>Status</th>
                    <th>Partner</th>
                    <th className="bia-amount-head">Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty-state-cell">
                        <div className="empty-state-container">
                          <div className="empty-state-icon">
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </div>
                          <div className="empty-state-text">
                            {view === VIEWS.waiting ? (
                              <>
                                <h3>Nothing waiting for check</h3>
                                <p>Every bunker invoice has been sent to finance.</p>
                                {data.total_deliveries > 0 && (
                                  <button
                                    type="button"
                                    className="btn-secondary bia-empty-action"
                                    onClick={() => setView(VIEWS.all)}
                                  >
                                    Show all bunker deliveries
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <h3>No bunker deliveries yet</h3>
                                <p>There are no bunker deliveries you can open.</p>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <DeliveryRows
                        key={row.delivery_id}
                        row={row}
                        isLastDelivery={index === rows.length - 1}
                        onOpen={openInvoice}
                        onOpenDelivery={openDelivery}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </Layout>
  );
};

export default BunkerInvoiceApprovals;
